import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpAppsecEditPermission } from '@internal/ksdp-permissions-common';
import {
  ApplicationComplianceHistoryEntry,
  ApplicationComplianceResponse,
  ApplicationComplianceThreat,
  ApplicationComplianceUsecase,
  ApplicationCountermeasure,
  ApplicationComplianceExceptionMessage,
  ComplianceRequirementStatus,
} from './types';
import { formatDateTime } from './formatDateTime';

interface RequirementDetailRouteProps {
  response: ApplicationComplianceResponse | null;
  detailBackButtonRef: RefObject<HTMLButtonElement>;
  renderRequirementDetail: (requirementId: string) => JSX.Element;
  setDraftStatus: Dispatch<SetStateAction<ComplianceRequirementStatus | null>>;
  setDraftSupportingLinks: Dispatch<SetStateAction<string>>;
  setDraftEvidenceDescription: Dispatch<SetStateAction<string>>;
  setDraftNaJustification: Dispatch<SetStateAction<string>>;
  setFormError: Dispatch<SetStateAction<string | null>>;
}

function RequirementDetailRoute({
  response,
  detailBackButtonRef,
  renderRequirementDetail,
  setDraftStatus,
  setDraftSupportingLinks,
  setDraftEvidenceDescription,
  setDraftNaJustification,
  setFormError,
}: RequirementDetailRouteProps) {
  const { requirementId } = useParams<{ requirementId: string }>();

  useEffect(() => {
    if (requirementId) {
      detailBackButtonRef.current?.focus();
    }

    const countermeasure = requirementId
      ? findCountermeasure(response, requirementId)
      : undefined;
    if (countermeasure) {
      setDraftStatus(countermeasure.status);
      setDraftSupportingLinks(countermeasure.supportingLinks ?? '');
      setDraftEvidenceDescription(countermeasure.evidenceDescription ?? '');
      setDraftNaJustification(countermeasure.naJustification ?? '');
    } else {
      setDraftStatus(null);
      setDraftSupportingLinks('');
      setDraftEvidenceDescription('');
      setDraftNaJustification('');
    }
    setFormError(null);
    // These state setters are stable; the renderer is intentionally omitted
    // so changing draft state does not reinitialize the current requirement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirementId, response]);

  return renderRequirementDetail(requirementId ?? '');
}

export interface ComplianceViewProps {
  ban: string;
  onBackToDashboard: () => void;
}

interface ThreatGroup {
  usecase: ApplicationComplianceUsecase;
  threat: ApplicationComplianceThreat;
  /** Requirements to render (narrowed when a search/label filter is active). */
  countermeasures: ApplicationCountermeasure[];
  /** Unfiltered requirements, so progress stays accurate while filtering. */
  allCountermeasures?: ApplicationCountermeasure[];
}

interface ComplianceProgress {
  done: number;
  total: number;
  pct: number;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

const RESOLVED_STATUSES: ReadonlySet<ComplianceRequirementStatus> = new Set([
  'implemented',
  'not_applicable',
]);

const STATUS_LABELS: Record<ComplianceRequirementStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  implemented: 'Implemented',
  not_applicable: 'Not applicable',
};

/** Value of the label filter dropdown: '' (All), 'automated', or a status. */
type RequirementLabelFilter = '' | 'automated' | ComplianceRequirementStatus;

const LABEL_FILTER_OPTIONS: ReadonlyArray<{
  value: RequirementLabelFilter;
  label: string;
}> = [
  { value: '', label: 'All' },
  { value: 'automated', label: 'Automated' },
  ...(Object.keys(STATUS_LABELS) as ComplianceRequirementStatus[]).map(
    status => ({ value: status, label: STATUS_LABELS[status] }),
  ),
];

function formatAuditTimestamp(value: string | null | undefined): string {
  if (!value) return 'N/A';
  return formatDateTime(value);
}

function formatAuditActor(value: string | null | undefined): string {
  if (!value) return 'N/A';
  return value.replace(/^user:default\//, '');
}

/** "Oct 07, 2026, 12:40 PM" — the conversation timestamp style. */
function formatMessageTimestamp(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

/** Up to two initials for the avatar: "devyansh.bhateja@kone.com" -> "DB". */
function authorInitials(displayName: string): string {
  const localPart = displayName.split('@')[0];
  const parts = localPart.split(/[._\-\s]+/).filter(Boolean);
  const letters =
    parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : localPart.slice(0, 2);
  return letters.toUpperCase() || '?';
}

function historyStatusLabel(status: ComplianceRequirementStatus): string {
  return STATUS_LABELS[status] ?? status;
}

function historyChangeTypeLabel(changeType: string): string {
  switch (changeType) {
    case 'baseline':
      return 'Baseline snapshot';
    case 'submission':
      return 'Evidence submission';
    case 'exception_opened':
      return 'Exception requested';
    case 'exception_message':
      return 'Case message';
    case 'exception_status':
      return 'Case status';
    default:
      return changeType;
  }
}

function historyStatusClass(status: ComplianceRequirementStatus): string {
  return status.replace('_', '-');
}

function historyLinks(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(/\r?\n/)
    .map(link => link.trim())
    .filter(link => /^https?:\/\//i.test(link));
}

/**
 * One renderable section of the Compliance module: either Baseline
 * Requirements (automatic, matrix-driven) or one manually-assigned Standard.
 * Kept distinct rather than flattened so each section's applicability
 * mechanism and identity stay visible in the UI.
 */
interface ComplianceSection {
  key: string;
  title: string;
  countermeasures: ApplicationCountermeasure[];
  usecases: ApplicationComplianceUsecase[];
}

function sectionsFromResponse(
  response: ApplicationComplianceResponse | null,
): ComplianceSection[] {
  if (!response) return [];
  const sections: ComplianceSection[] = [];
  if (response.baseline.applicable) {
    sections.push({
      key: 'baseline',
      title: 'Baseline Requirements',
      countermeasures: response.baseline.countermeasures,
      usecases: response.baseline.usecases,
    });
  }
  response.assignedStandards.forEach(standard => {
    sections.push({
      key: `standard:${standard.standardId}`,
      title: standard.standardName,
      countermeasures: standard.countermeasures,
      usecases: standard.usecases,
    });
  });
  return sections;
}

function findCountermeasure(
  response: ApplicationComplianceResponse | null,
  requirementId: string,
): ApplicationCountermeasure | undefined {
  if (!response) return undefined;
  const inBaseline = response.baseline.countermeasures.find(
    item => item.requirementId === requirementId,
  );
  if (inBaseline) return inBaseline;
  for (const standard of response.assignedStandards) {
    const match = standard.countermeasures.find(
      item => item.requirementId === requirementId,
    );
    if (match) return match;
  }
  return undefined;
}

function usecasesForCountermeasure(
  response: ApplicationComplianceResponse | null,
  requirementId: string,
): ApplicationComplianceUsecase[] {
  if (!response) return [];
  if (
    response.baseline.countermeasures.some(
      item => item.requirementId === requirementId,
    )
  ) {
    return response.baseline.usecases;
  }
  const standard = response.assignedStandards.find(item =>
    item.countermeasures.some(cm => cm.requirementId === requirementId),
  );
  return standard?.usecases ?? [];
}

/** Apply `updater` to the countermeasure matching `requirementId`, wherever
 * it lives (Baseline or one of the assigned Standards), leaving every other
 * section untouched. */
function updateCountermeasureInResponse(
  response: ApplicationComplianceResponse,
  requirementId: string,
  updater: (item: ApplicationCountermeasure) => ApplicationCountermeasure,
): ApplicationComplianceResponse {
  const mapList = (items: ApplicationCountermeasure[]) =>
    items.map(item =>
      item.requirementId === requirementId ? updater(item) : item,
    );
  return {
    ...response,
    baseline: {
      ...response.baseline,
      countermeasures: mapList(response.baseline.countermeasures),
    },
    assignedStandards: response.assignedStandards.map(standard => ({
      ...standard,
      countermeasures: mapList(standard.countermeasures),
    })),
  };
}

function countResolved(
  countermeasures: ApplicationCountermeasure[],
): ComplianceProgress {
  const total = countermeasures.length;
  const done = countermeasures.filter(countermeasure =>
    RESOLVED_STATUSES.has(countermeasure.status),
  ).length;
  return {
    done,
    total,
    pct: total > 0 ? Math.round((done / total) * 100) : 0,
  };
}

function resolveBreadcrumb(
  countermeasure: ApplicationCountermeasure,
  usecases: ApplicationComplianceUsecase[],
): { usecaseName: string; threatName: string | null } {
  for (const usecase of usecases) {
    for (const threat of usecase.threats) {
      const explicitMatch =
        countermeasure.usecaseRef === usecase.ref &&
        countermeasure.threatRef === threat.ref;
      if (explicitMatch) {
        return { usecaseName: usecase.name, threatName: threat.name };
      }
    }
  }

  for (const usecase of usecases) {
    for (const threat of usecase.threats) {
      const refMatch = threat.countermeasureRefs.some(
        reference => reference.ref === countermeasure.ref,
      );
      if (refMatch) {
        return { usecaseName: usecase.name, threatName: threat.name };
      }
    }
  }

  return { usecaseName: 'Unassigned', threatName: null };
}

/**
 * Per-application Baseline Requirements view.
 *
 * The API has already resolved the application's app type and impact level,
 * selected active requirements, and merged this BAN's persisted status. This
 * component deliberately duplicates only the standalone library's grouping
 * presentation: requirement content is read-only, while status/evidence
 * changes are staged in the detail form and submitted atomically.
 */
export function ComplianceView({
  ban,
  onBackToDashboard,
}: ComplianceViewProps) {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // UI-only gate: hides status edits, exception requests/replies and
  // application links. Every write route is enforced by the backend router.
  const { allowed: canEdit } = usePermission({
    permission: ksdpAppsecEditPermission,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isRequirementDetail = /\/compliance\/[^/]+$/.test(location.pathname);
  const requirementDetailMatch = location.pathname.match(
    /\/compliance\/([^/]+)$/,
  );
  const currentRequirementId = requirementDetailMatch
    ? decodeURIComponent(requirementDetailMatch[1])
    : null;

  const [response, setResponse] =
    useState<ApplicationComplianceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const detailBackButtonRef = useRef<HTMLButtonElement>(null);
  const [savingEntryId, setSavingEntryId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [draftStatus, setDraftStatus] =
    useState<ComplianceRequirementStatus | null>(null);
  const [draftSupportingLinks, setDraftSupportingLinks] = useState('');
  const [draftEvidenceDescription, setDraftEvidenceDescription] = useState('');
  const [draftNaJustification, setDraftNaJustification] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [requirementSearch, setRequirementSearch] = useState('');
  const [requirementLabelFilter, setRequirementLabelFilter] =
    useState<RequirementLabelFilter>('');
  const [exceptionExpanded, setExceptionExpanded] = useState(false);
  const [exceptionMessages, setExceptionMessages] = useState<
    ApplicationComplianceExceptionMessage[]
  >([]);
  const [loadingExceptionMessages, setLoadingExceptionMessages] =
    useState(false);
  const [exceptionExplanation, setExceptionExplanation] = useState('');
  const [exceptionReplyBody, setExceptionReplyBody] = useState('');
  const [savingExceptionRequest, setSavingExceptionRequest] = useState(false);
  const [savingExceptionReply, setSavingExceptionReply] = useState(false);
  const [showExceptionForm, setShowExceptionForm] = useState(false);

  // Application linking state
  const [linkResponse, setLinkResponse] = useState<{
    linkGroupId: string;
    members: { ban: string; name: string }[];
  } | null>(null);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [targetBan, setTargetBan] = useState('');
  const [targetBanName, setTargetBanName] = useState('');
  const [importFrom, setImportFrom] = useState('');
  const [linkStep, setLinkStep] = useState<'search' | 'import'>('search');
  const [savingLink, setSavingLink] = useState(false);
  const [linkFormError, setLinkFormError] = useState<string | null>(null);

  // Target-BAN search (link picker) — same debounced list_apps pattern
  // BusinessApplicationsPage.tsx uses for its own BAN filter, scoped here to
  // BAN only (no lifecycle/impact/BU/department dropdowns — this is a modal,
  // not the full applications list).
  const [banSearchTerm, setBanSearchTerm] = useState('');
  const [debouncedBanSearchTerm, setDebouncedBanSearchTerm] = useState('');
  const [banSearchResults, setBanSearchResults] = useState<
    { ban: string; name: string }[]
  >([]);
  const [loadingBanSearch, setLoadingBanSearch] = useState(false);
  const banSearchRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadCompliance = async () => {
      setLoading(true);
      setError(null);
      setResponse(null);
      setExpandedRefs(new Set());
      setHistoryExpanded(false);
      setRowErrors({});
      setRequirementSearch('');
      setRequirementLabelFilter('');
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');
        const res = await fetchApi.fetch(
          `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance`,
        );
        if (!res.ok) {
          throw new Error(
            `Failed to load application compliance (${res.status})`,
          );
        }
        const data: ApplicationComplianceResponse = await res.json();
        if (!cancelled) {
          setResponse(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            errorMessage(loadError, 'Could not load compliance information.'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCompliance();
    return () => {
      cancelled = true;
    };
  }, [ban, discoveryApi, fetchApi]);

  useEffect(() => {
    let cancelled = false;

    const loadLinks = async () => {
      setLoadingLinks(true);
      setLinkError(null);
      setLinkResponse(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');
        const res = await fetchApi.fetch(
          `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance-links`,
        );
        if (!res.ok) {
          throw new Error(`Failed to load application links (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setLinkResponse(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setLinkError(
            errorMessage(loadError, 'Could not load link information.'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingLinks(false);
        }
      }
    };

    loadLinks();
    return () => {
      cancelled = true;
    };
  }, [ban, discoveryApi, fetchApi]);

  // Debounce the link picker's BAN search input (same 500ms delay
  // BusinessApplicationsPage.tsx uses for its own BAN filter).
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedBanSearchTerm(banSearchTerm);
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [banSearchTerm]);

  // Fetch matching applications for the link picker's BAN search, via the
  // same ksdp-list-apps endpoint BusinessApplicationsPage.tsx already uses,
  // filtered to BAN only.
  useEffect(() => {
    if (!showLinkPicker || linkStep !== 'search') {
      return () => {};
    }
    if (!debouncedBanSearchTerm.trim()) {
      setBanSearchResults([]);
      setLoadingBanSearch(false);
      return () => {};
    }

    let cancelled = false;
    const requestId = ++banSearchRequestRef.current;

    const searchApplications = async () => {
      setLoadingBanSearch(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');
        const params = new URLSearchParams({
          ban: debouncedBanSearchTerm.trim(),
          limit: '20',
        });
        const res = await fetchApi.fetch(`${baseUrl}/applications?${params}`);
        if (!res.ok) {
          throw new Error(`Failed to search applications (${res.status})`);
        }
        const data = await res.json();
        if (cancelled || requestId !== banSearchRequestRef.current) {
          return;
        }
        const rows: { number?: string; ban?: string; name?: string }[] =
          data.data ?? [];
        setBanSearchResults(
          rows
            .map(row => ({
              ban: row.ban ?? row.number ?? '',
              name: row.name ?? '',
            }))
            .filter(row => row.ban && row.ban !== ban),
        );
      } catch {
        if (!cancelled && requestId === banSearchRequestRef.current) {
          setBanSearchResults([]);
        }
      } finally {
        if (!cancelled && requestId === banSearchRequestRef.current) {
          setLoadingBanSearch(false);
        }
      }
    };

    void searchApplications();
    return () => {
      cancelled = true;
    };
  }, [
    ban,
    debouncedBanSearchTerm,
    discoveryApi,
    fetchApi,
    showLinkPicker,
    linkStep,
  ]);

  const toggleExpanded = (ref: string) => {
    setExpandedRefs(previous => {
      const next = new Set(previous);
      if (next.has(ref)) {
        next.delete(ref);
      } else {
        next.add(ref);
      }
      return next;
    });
  };

  const loadExceptionMessages = async (requirementId: string) => {
    setLoadingExceptionMessages(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(
          ban,
        )}/compliance/${encodeURIComponent(requirementId)}/exception`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load exception messages (${res.status})`);
      }
      // The Lambda wraps the case as { exceptionCase: { ..., messages } },
      // and exceptionCase is null when no case exists.
      const data: {
        exceptionCase?: {
          messages?: ApplicationComplianceExceptionMessage[];
        } | null;
      } = await res.json();
      setExceptionMessages(data.exceptionCase?.messages ?? []);
    } catch (loadError) {
      setFormError(
        errorMessage(loadError, 'Could not load exception messages.'),
      );
      setExceptionMessages([]);
    } finally {
      setLoadingExceptionMessages(false);
    }
  };

  const handleRequestException = async (
    countermeasure: ApplicationCountermeasure,
  ) => {
    if (savingExceptionRequest || !exceptionExplanation.trim()) {
      return;
    }

    setSavingExceptionRequest(true);
    setFormError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(
          ban,
        )}/compliance/${encodeURIComponent(
          countermeasure.requirementId,
        )}/exception`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ explanation: exceptionExplanation }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.error || `Failed to request exception (${res.status})`,
        );
      }

      // Refetch compliance data to get the new exception case
      const complianceRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance`,
      );
      if (complianceRes.ok) {
        const data: ApplicationComplianceResponse = await complianceRes.json();
        setResponse(data);
      }

      setExceptionExplanation('');
      setShowExceptionForm(false);
    } catch (saveError) {
      const message = errorMessage(saveError, 'Could not request exception.');
      setFormError(message);
    } finally {
      setSavingExceptionRequest(false);
    }
  };

  const handleReplyToException = async (
    countermeasure: ApplicationCountermeasure,
  ) => {
    if (savingExceptionReply || !exceptionReplyBody.trim()) {
      return;
    }

    setSavingExceptionReply(true);
    setFormError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(
          ban,
        )}/compliance/${encodeURIComponent(
          countermeasure.requirementId,
        )}/exception/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: exceptionReplyBody }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to send reply (${res.status})`);
      }

      setExceptionReplyBody('');
      // Reload messages to show the new reply
      await loadExceptionMessages(countermeasure.requirementId);

      // A reply to a rejected case reopens it, so refresh the case status
      // (and message count) shown in the toggle and heading as well.
      const complianceRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance`,
      );
      if (complianceRes.ok) {
        const data: ApplicationComplianceResponse = await complianceRes.json();
        setResponse(data);
      }
    } catch (saveError) {
      const message = errorMessage(saveError, 'Could not send reply.');
      setFormError(message);
    } finally {
      setSavingExceptionReply(false);
    }
  };

  // Silent refetch of the compliance data. Unlike the initial load effect it
  // does not clear `response` or toggle `loading`, so the view stays visible.
  // Failures are swallowed on purpose: callers use it after a mutation that
  // already succeeded, so the data they hold must not be rolled back.
  const refreshCompliance = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const complianceRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance`,
      );
      if (complianceRes.ok) {
        const data: ApplicationComplianceResponse = await complianceRes.json();
        setResponse(data);
      }
    } catch {
      // Keep the current (optimistically updated) data when the refetch fails.
    }
  };

  const handleSubmit = async (countermeasure: ApplicationCountermeasure) => {
    if (
      !response ||
      countermeasure.automation?.managed ||
      savingEntryId === countermeasure.requirementId
    ) {
      return;
    }

    const status = draftStatus ?? countermeasure.status;
    const evidenceDescriptionRequired = status === 'implemented';
    if (evidenceDescriptionRequired && !draftEvidenceDescription.trim()) {
      setFormError('Evidence description is required for Implemented.');
      return;
    }
    if (status === 'not_applicable' && !draftNaJustification.trim()) {
      setFormError('Justification is required for Not Applicable.');
      return;
    }

    const payload = {
      status,
      supportingLinks: draftSupportingLinks.trim() || undefined,
      evidenceDescription:
        status === 'in_progress' || status === 'implemented'
          ? draftEvidenceDescription.trim() || undefined
          : undefined,
      naJustification:
        status === 'not_applicable'
          ? draftNaJustification.trim() || undefined
          : undefined,
    };
    const previousCountermeasure = countermeasure;
    setFormError(null);
    setSavingEntryId(countermeasure.requirementId);
    setRowErrors(previous => {
      const next = { ...previous };
      delete next[countermeasure.requirementId];
      return next;
    });
    setResponse(previous =>
      previous
        ? updateCountermeasureInResponse(
            previous,
            countermeasure.requirementId,
            item => ({
              ...item,
              status,
              supportingLinks:
                draftSupportingLinks.trim() || item.supportingLinks || null,
              // Mirror the backend: a field hidden for the chosen status is
              // cleared, not carried over from the previous status.
              evidenceDescription:
                status === 'in_progress' || status === 'implemented'
                  ? draftEvidenceDescription.trim() ||
                    item.evidenceDescription ||
                    null
                  : null,
              naJustification:
                status === 'not_applicable'
                  ? draftNaJustification.trim() || item.naJustification || null
                  : null,
            }),
          )
        : previous,
    );

    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(
          ban,
        )}/compliance/${encodeURIComponent(countermeasure.requirementId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save (${res.status})`);
      }

      // The optimistic update above only covers the fields the user edited.
      // Pull the server's version so fields it writes itself (history,
      // updatedBy/updatedAt, automation) are current without a page reload.
      await refreshCompliance();
    } catch (saveError) {
      setResponse(previous =>
        previous
          ? updateCountermeasureInResponse(
              previous,
              countermeasure.requirementId,
              () => previousCountermeasure,
            )
          : previous,
      );
      const message = errorMessage(
        saveError,
        'Could not save this requirement status.',
      );
      setFormError(message);
      setRowErrors(previous => ({
        ...previous,
        [countermeasure.requirementId]: message,
      }));
    } finally {
      setSavingEntryId(null);
    }
  };

  const resetLinkPickerState = () => {
    setShowLinkPicker(false);
    setTargetBan('');
    setTargetBanName('');
    setImportFrom('');
    setLinkStep('search');
    setLinkFormError(null);
    setBanSearchTerm('');
    setDebouncedBanSearchTerm('');
    setBanSearchResults([]);
  };

  const handleCreateApplicationLink = async () => {
    if (!targetBan.trim() || !importFrom.trim() || savingLink) {
      return;
    }

    setSavingLink(true);
    setLinkFormError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance-links`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetBan, importFrom }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.error || `Failed to create application link (${res.status})`,
        );
      }

      // Refetch link data to get the updated state
      const linkRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance-links`,
      );
      if (linkRes.ok) {
        const data = await linkRes.json();
        setLinkResponse(data);
      }

      // Refetch compliance data to see synced status
      const complianceRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance`,
      );
      if (complianceRes.ok) {
        const data: ApplicationComplianceResponse = await complianceRes.json();
        setResponse(data);
      }

      resetLinkPickerState();
    } catch (saveError) {
      const message = errorMessage(
        saveError,
        'Could not create application link.',
      );
      setLinkFormError(message);
    } finally {
      setSavingLink(false);
    }
  };

  const handleDeleteApplicationLink = async () => {
    if (savingLink) {
      return;
    }

    const confirmMessage = linkResponse
      ? `Unlink this application from: ${linkResponse.members
          .filter(m => m.ban !== ban)
          .map(m => m.name)
          .join(', ')}?`
      : 'Unlink this application?';

    // Simple confirm guard for a destructive-feeling action, matching the
    // same pattern already used for unlink/archive actions in
    // CompliancePage.tsx (Feature A).
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSavingLink(true);
    setLinkFormError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance-links`,
        {
          method: 'DELETE',
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.error || `Failed to unlink application (${res.status})`,
        );
      }

      // Refetch link data to get the updated state
      const linkRes = await fetchApi.fetch(
        `${baseUrl}/applications/${encodeURIComponent(ban)}/compliance-links`,
      );
      if (linkRes.ok) {
        const data = await linkRes.json();
        setLinkResponse(data);
      }
    } catch (saveError) {
      const message = errorMessage(saveError, 'Could not unlink application.');
      setLinkFormError(message);
    } finally {
      setSavingLink(false);
    }
  };

  const handleSelectTargetBan = (selected: { ban: string; name: string }) => {
    setTargetBan(selected.ban);
    setTargetBanName(selected.name);
    setLinkStep('import');
    setLinkFormError(null);
  };

  const requirementSearchTerm = requirementSearch.trim().toLocaleLowerCase();
  const isRequirementFilterActive =
    requirementSearchTerm.length > 0 || requirementLabelFilter !== '';
  const matchesLabelFilter = (countermeasure: ApplicationCountermeasure) => {
    if (requirementLabelFilter === '') return true;
    if (requirementLabelFilter === 'automated') {
      return countermeasure.automation?.managed === true;
    }
    return countermeasure.status === requirementLabelFilter;
  };
  const matchesRequirement = (countermeasure: ApplicationCountermeasure) => {
    if (!matchesLabelFilter(countermeasure)) return false;
    if (requirementSearchTerm.length === 0) return true;
    return [
      countermeasure.name,
      countermeasure.requirementId,
      countermeasure.ref,
      countermeasure.text,
      ...countermeasure.standardRefs,
    ].some(value => value.toLocaleLowerCase().includes(requirementSearchTerm));
  };
  const renderProgress = (
    countermeasures: ApplicationCountermeasure[],
    inline = false,
  ) => {
    const progress = countResolved(countermeasures);
    const label =
      progress.total === 0
        ? 'No applicable requirements'
        : `${progress.done} of ${progress.total} complete (${progress.pct}%)`;
    return (
      <span
        className={`application-compliance-progress${
          inline ? ' application-compliance-progress--inline' : ''
        }`}
        aria-label={label}
      >
        <span
          className="application-compliance-progress-bar"
          aria-hidden="true"
        >
          <span
            className="application-compliance-progress-bar-fill"
            style={{ width: `${progress.pct}%` }}
          />
        </span>
        <span className="application-compliance-progress-label">{label}</span>
      </span>
    );
  };

  const renderCountermeasureRow = (
    countermeasure: ApplicationCountermeasure,
  ) => (
    <div
      key={countermeasure.requirementId}
      className="application-compliance-requirement"
    >
      <div className="application-compliance-requirement-header">
        <button
          type="button"
          className="application-compliance-requirement-toggle"
          onClick={() => {
            setHistoryExpanded(false);
            navigate(encodeURIComponent(countermeasure.requirementId));
          }}
          aria-label={`Open requirement ${countermeasure.name}. ${
            countermeasure.automation?.managed ? 'Automated. ' : ''
          }Status: ${STATUS_LABELS[countermeasure.status]}`}
        >
          <span className="application-compliance-requirement-name">
            {countermeasure.name}
          </span>
          <span className="application-compliance-requirement-badges">
            {countermeasure.automation?.managed === true && (
              <span className="application-compliance-status-pill application-compliance-status-pill--automated">
                Automated
              </span>
            )}
            <span
              className={`application-compliance-status-pill application-compliance-status-pill--${countermeasure.status.replace(
                '_',
                '-',
              )}`}
            >
              {STATUS_LABELS[countermeasure.status]}
            </span>
          </span>
          <span className="application-compliance-chevron" aria-hidden="true">
            &#8250;
          </span>
        </button>
      </div>
    </div>
  );

  const renderHistory = (countermeasure: ApplicationCountermeasure) => {
    const entries: ApplicationComplianceHistoryEntry[] =
      countermeasure.history ?? [];
    const historyCountLabel = `${entries.length} snapshot${
      entries.length === 1 ? '' : 's'
    }`;
    const panelId = `application-compliance-history-${countermeasure.requirementId}`;
    const headingId = `${panelId}-heading`;

    return (
      <>
        <button
          type="button"
          className="application-compliance-history-toggle"
          aria-expanded={historyExpanded}
          aria-controls={panelId}
          onClick={() => setHistoryExpanded(previous => !previous)}
        >
          <span className="application-compliance-history-toggle-label">
            {historyExpanded
              ? 'Hide historical evidence'
              : 'View historical evidence'}
          </span>
          <span className="compliance-history-count">{historyCountLabel}</span>
          <span
            className="application-compliance-history-toggle-chevron"
            aria-hidden="true"
          >
            {historyExpanded ? '\u25B2' : '\u25BC'}
          </span>
        </button>
        <div
          id={panelId}
          className="application-compliance-history-panel"
          hidden={!historyExpanded}
        >
          <section
            className="application-compliance-history"
            aria-labelledby={headingId}
          >
            <div className="application-compliance-history-heading">
              <div>
                <h6 id={headingId}>Historical evidence</h6>
                <p>
                  Previous evidence and status snapshots for this requirement.
                </p>
              </div>
            </div>
            {entries.length > 0 ? (
              <div className="compliance-history-list">
                {entries.map(entry => {
                  const links = historyLinks(entry.supportingLinks);
                  const changeLabel = historyChangeTypeLabel(entry.changeType);
                  const isExceptionEntry = [
                    'exception_opened',
                    'exception_message',
                    'exception_status',
                  ].includes(entry.changeType);
                  const displayAuthor =
                    entry.actorRole === 'admin'
                      ? 'Compliance Admin'
                      : formatAuditActor(entry.updatedBy);

                  return (
                    <article
                      key={entry.historyId}
                      className="compliance-history-entry"
                    >
                      <div className="compliance-history-entry-header">
                        <div>
                          <span
                            className={`compliance-history-change-badge compliance-history-change-badge--${entry.changeType.replace(
                              '_',
                              '-',
                            )}`}
                          >
                            {changeLabel}
                          </span>
                          {!isExceptionEntry && (
                            <span
                              className={`compliance-history-status compliance-history-status--${historyStatusClass(
                                entry.status,
                              )}`}
                            >
                              {historyStatusLabel(entry.status)}
                            </span>
                          )}
                        </div>
                        <time dateTime={entry.recordedAt}>
                          {formatAuditTimestamp(entry.recordedAt)}
                        </time>
                      </div>
                      <dl className="compliance-history-metadata">
                        <div>
                          <dt>Changed by</dt>
                          <dd>{displayAuthor}</dd>
                        </div>
                        <div>
                          <dt>History ID</dt>
                          <dd>{entry.historyId}</dd>
                        </div>
                        {entry.exceptionCaseId && (
                          <div>
                            <dt>Exception Case</dt>
                            <dd>{entry.exceptionCaseId}</dd>
                          </div>
                        )}
                      </dl>
                      {isExceptionEntry && entry.exceptionDetail && (
                        <div className="compliance-history-evidence-block">
                          <h6>Content</h6>
                          <p>{entry.exceptionDetail}</p>
                        </div>
                      )}
                      {!isExceptionEntry && entry.evidenceDescription && (
                        <div className="compliance-history-evidence-block">
                          <h6>Evidence description</h6>
                          <p>{entry.evidenceDescription}</p>
                        </div>
                      )}
                      {!isExceptionEntry && entry.naJustification && (
                        <div className="compliance-history-evidence-block">
                          <h6>Not-applicable justification</h6>
                          <p>{entry.naJustification}</p>
                        </div>
                      )}
                      {!isExceptionEntry && (
                        <div className="compliance-history-evidence-block">
                          <h6>Supporting links</h6>
                          {links.length > 0 ? (
                            <ul className="compliance-history-links">
                              {links.map(link => (
                                <li key={link}>
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {link}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="compliance-history-empty">
                              No supporting links recorded
                            </span>
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="compliance-history-empty-state">
                No historical evidence has been recorded for this requirement.
              </p>
            )}
          </section>
        </div>
      </>
    );
  };

  const renderExceptionCase = (countermeasure: ApplicationCountermeasure) => {
    const exceptionCase = countermeasure.exceptionCase;
    if (!exceptionCase) return null;

    const panelId = `application-compliance-exception-${countermeasure.requirementId}`;
    const headingId = `${panelId}-heading`;

    const statusPill = (
      <span
        className={`application-compliance-status-pill application-compliance-status-pill--${exceptionCase.status}`}
      >
        {exceptionCase.status === 'open' ? 'Open' : 'Rejected'}
      </span>
    );

    const messageCountLabel = `${exceptionCase.messageCount} message${
      exceptionCase.messageCount === 1 ? '' : 's'
    }`;

    // A requester reply to a rejected case reopens it (backend add_message),
    // but only while the reopen window is still running.
    const isRejected = exceptionCase.status === 'rejected';
    const canReply =
      canEdit &&
      (exceptionCase.status === 'open' ||
        (isRejected && (exceptionCase.daysRemaining ?? 0) > 0));

    return (
      <>
        <button
          type="button"
          className="application-compliance-exception-toggle"
          aria-expanded={exceptionExpanded}
          aria-controls={panelId}
          onClick={() => {
            setExceptionExpanded(previous => !previous);
            if (!exceptionExpanded) {
              void loadExceptionMessages(countermeasure.requirementId);
            }
          }}
        >
          <span className="application-compliance-exception-toggle-label">
            {exceptionExpanded ? 'Hide exception case' : 'Exception Case'}
          </span>
          <span className="compliance-exception-status">{statusPill}</span>
          <span className="compliance-exception-count">
            {messageCountLabel}
          </span>
          <span
            className="application-compliance-exception-toggle-chevron"
            aria-hidden="true"
          >
            {exceptionExpanded ? '\u25B2' : '\u25BC'}
          </span>
        </button>
        <div
          id={panelId}
          className="application-compliance-exception-panel"
          hidden={!exceptionExpanded}
        >
          <section
            className="application-compliance-exception"
            aria-labelledby={headingId}
          >
            <div className="application-compliance-exception-heading">
              <div>
                <h6 id={headingId}>Exception conversation</h6>
                <p>
                  Case {exceptionCase.caseId}. Keep the request, questions, and
                  supporting proof together in this case.
                  {isRejected &&
                    typeof exceptionCase.daysRemaining === 'number' && (
                      <strong>
                        {' '}
                        Days remaining to reopen: {exceptionCase.daysRemaining}.
                      </strong>
                    )}
                </p>
              </div>
              {statusPill}
            </div>
            {loadingExceptionMessages ? (
              <p className="loading-text">Loading messages...</p>
            ) : (
              <div className="compliance-exception-conversation">
                {exceptionMessages.length > 0 ? (
                  <div
                    className="compliance-exception-messages"
                    role="log"
                    aria-live="polite"
                    aria-label="Exception case conversation"
                  >
                    {exceptionMessages.map((message, index) => {
                      const isAdmin = message.authorRole === 'admin';
                      const displayName = isAdmin
                        ? 'Compliance Admin'
                        : formatAuditActor(message.author);
                      return (
                        <article
                          key={`${message.postedAt}-${index}`}
                          className={`compliance-exception-message compliance-exception-message--${
                            isAdmin ? 'admin' : 'requester'
                          }`}
                        >
                          <div
                            className="compliance-exception-message-avatar"
                            aria-hidden="true"
                          >
                            {authorInitials(displayName)}
                          </div>
                          <div className="compliance-exception-message-bubble">
                            <div className="compliance-exception-message-meta">
                              <strong>{displayName}</strong>
                              <time dateTime={message.postedAt}>
                                {formatMessageTimestamp(message.postedAt)}
                              </time>
                            </div>
                            <p>{message.body}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="compliance-exception-empty-state">
                    No messages in this exception case.
                  </p>
                )}

                {canReply && (
                  <div className="compliance-exception-reply">
                    <label htmlFor={`${panelId}-reply`}>
                      {isRejected ? 'Reply to reopen this case' : 'Add a reply'}
                    </label>
                    <textarea
                      id={`${panelId}-reply`}
                      value={exceptionReplyBody}
                      rows={3}
                      placeholder="Add context, answer an admin question, or provide more evidence..."
                      disabled={savingExceptionReply}
                      onChange={event => {
                        setExceptionReplyBody(event.target.value);
                        setFormError(null);
                      }}
                    />
                    <div className="compliance-exception-reply-actions">
                      <span>
                        {isRejected
                          ? 'Sending a reply reopens this case for review.'
                          : 'Replies are recorded against this case.'}
                      </span>
                      <button
                        type="button"
                        className="action-button"
                        disabled={
                          savingExceptionReply || !exceptionReplyBody.trim()
                        }
                        onClick={() =>
                          void handleReplyToException(countermeasure)
                        }
                      >
                        Send reply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </>
    );
  };

  const renderExceptionRequestForm = (
    countermeasure: ApplicationCountermeasure,
  ) => {
    if (!showExceptionForm || !canEdit) return null;

    return (
      <div className="application-compliance-exception-form">
        <h6>Request Exception</h6>
        <p>
          Explain why this requirement should not apply to your application:
        </p>
        <label className="application-compliance-field">
          <span>Explanation</span>
          <textarea
            value={exceptionExplanation}
            rows={4}
            placeholder="Provide a detailed explanation for the exception request..."
            maxLength={4000}
            disabled={savingExceptionRequest}
            onChange={event => {
              setExceptionExplanation(event.target.value);
              setFormError(null);
            }}
          />
          <small className="application-compliance-field-help">
            {exceptionExplanation.length}/4000 characters
          </small>
        </label>
        <div className="application-compliance-exception-form-actions">
          <button
            type="button"
            className="action-button"
            disabled={savingExceptionRequest || !exceptionExplanation.trim()}
            onClick={() => void handleRequestException(countermeasure)}
          >
            Submit Exception Request
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={savingExceptionRequest}
            onClick={() => {
              setShowExceptionForm(false);
              setExceptionExplanation('');
              setFormError(null);
            }}
          >
            Cancel
          </button>
        </div>
        {formError && (
          <p className="application-compliance-form-error">{formError}</p>
        )}
      </div>
    );
  };

  const renderRequirementDetail = (requirementId: string) => {
    const countermeasure = findCountermeasure(response, requirementId);
    if (!countermeasure) {
      return (
        <div className="application-compliance-detail application-compliance-detail--empty">
          <p className="compliance-empty-state">Requirement not found.</p>
        </div>
      );
    }

    const breadcrumb = resolveBreadcrumb(
      countermeasure,
      usecasesForCountermeasure(response, requirementId),
    );
    const rowError = rowErrors[countermeasure.requirementId];
    const currentDraftStatus = draftStatus ?? countermeasure.status;
    const showEvidenceForm = currentDraftStatus !== 'not_started';
    const hasExistingEvidence = Boolean(
      countermeasure.supportingLinks?.trim() ||
        countermeasure.evidenceDescription?.trim() ||
        countermeasure.naJustification?.trim(),
    );
    let submitButtonLabel = 'Save status';
    if (showEvidenceForm) {
      submitButtonLabel = hasExistingEvidence
        ? 'Update evidence'
        : 'Submit evidence';
    }
    const isAutomated = countermeasure.automation?.managed === true;
    // Compare the length explicitly: `{array?.length && ...}` renders a stray
    // "0" in React when the array is empty.
    const linkedRequirementList = countermeasure.linkedRequirements ?? [];
    const currentSupportingLinks = historyLinks(countermeasure.supportingLinks);
    return (
      <div className="application-compliance-detail">
        <div
          className="application-compliance-breadcrumb"
          aria-label="Requirement location"
        >
          <span>{breadcrumb.usecaseName}</span>
          {breadcrumb.threatName && (
            <>
              <span aria-hidden="true"> &#8594; </span>
              <span>{breadcrumb.threatName}</span>
            </>
          )}
          <span aria-hidden="true"> &#8594; </span>
          <span>{countermeasure.name}</span>
        </div>
        <h5 className="application-compliance-detail-title">
          {countermeasure.name}
        </h5>
        <div className="application-compliance-detail-content">
          <p className="application-compliance-description">
            {countermeasure.text}
          </p>
          {countermeasure.links.length > 0 && (
            <ul className="application-compliance-links">
              {countermeasure.links.map(link => (
                <li key={`${countermeasure.requirementId}-${link.url}`}>
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {countermeasure.standardRefs.length > 0 && (
            <div className="application-compliance-standards">
              {countermeasure.standardRefs.map(reference => (
                <span
                  key={reference}
                  className="application-compliance-standard-chip"
                >
                  {reference}
                </span>
              ))}
            </div>
          )}
        </div>
        {linkedRequirementList.length > 0 && (
          <div className="application-compliance-linked-note">
            This requirement is linked to the same requirement in:{' '}
            {linkedRequirementList
              .map(linked => linked.standardName)
              .join(', ')}
          </div>
        )}
        {isAutomated && (
          <section
            className="application-compliance-automation-panel"
            aria-label="Automated compliance status"
          >
            <p className="application-compliance-automation-status">
              Status: <strong>{STATUS_LABELS[countermeasure.status]}</strong>
            </p>
            {countermeasure.automation?.message ? (
              <p className="application-compliance-automation-message">
                {countermeasure.automation.message}
              </p>
            ) : (
              <>
                {countermeasure.evidenceDescription && (
                  <div className="application-compliance-readonly-evidence">
                    <h6>Evidence description</h6>
                    <p>{countermeasure.evidenceDescription}</p>
                  </div>
                )}
                {currentSupportingLinks.length > 0 && (
                  <div className="application-compliance-readonly-evidence">
                    <h6>Supporting links</h6>
                    <ul className="application-compliance-links">
                      {currentSupportingLinks.map(link => (
                        <li key={link}>
                          <a href={link} target="_blank" rel="noreferrer">
                            {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {/* State 1: Managed automation with no exception case - the request
                is triggered from the "Request an exception" button in the
                page header; the form itself still renders inline here. */}
            {!countermeasure.exceptionCase && showExceptionForm && (
              <div className="application-compliance-exception-request">
                {renderExceptionRequestForm(countermeasure)}
              </div>
            )}
          </section>
        )}
        {!isAutomated && !canEdit && (
          <section
            className="application-compliance-automation-panel"
            aria-label="Compliance status"
          >
            <p className="application-compliance-automation-status">
              Status: <strong>{STATUS_LABELS[countermeasure.status]}</strong>
            </p>
            {countermeasure.evidenceDescription && (
              <div className="application-compliance-readonly-evidence">
                <h6>Evidence description</h6>
                <p>{countermeasure.evidenceDescription}</p>
              </div>
            )}
            {countermeasure.naJustification && (
              <div className="application-compliance-readonly-evidence">
                <h6>Justification</h6>
                <p>{countermeasure.naJustification}</p>
              </div>
            )}
            {currentSupportingLinks.length > 0 && (
              <div className="application-compliance-readonly-evidence">
                <h6>Supporting links</h6>
                <ul className="application-compliance-links">
                  {currentSupportingLinks.map(link => (
                    <li key={link}>
                      <a href={link} target="_blank" rel="noreferrer">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
        {!isAutomated && canEdit && (
          <form
            className="application-compliance-evidence-form"
            noValidate
            onSubmit={event => {
              event.preventDefault();
              void handleSubmit(countermeasure);
            }}
          >
            <label className="application-compliance-field">
              <span>Status</span>
              <select
                className="application-compliance-status-select"
                value={currentDraftStatus}
                disabled={savingEntryId === countermeasure.requirementId}
                onChange={event => {
                  setDraftStatus(
                    event.target.value as ComplianceRequirementStatus,
                  );
                  setFormError(null);
                }}
              >
                {(
                  Object.keys(STATUS_LABELS) as ComplianceRequirementStatus[]
                ).map(status => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </label>

            {showEvidenceForm && (
              <div className="application-compliance-evidence-fields">
                <label className="application-compliance-field">
                  <span>Supporting Links</span>
                  <textarea
                    value={draftSupportingLinks}
                    rows={3}
                    placeholder="Add one or more supporting links"
                    disabled={savingEntryId === countermeasure.requirementId}
                    onChange={event => {
                      setDraftSupportingLinks(event.target.value);
                      setFormError(null);
                    }}
                  />
                </label>
                {(currentDraftStatus === 'in_progress' ||
                  currentDraftStatus === 'implemented') && (
                  <label className="application-compliance-field">
                    <span>Evidence Description</span>
                    <textarea
                      value={draftEvidenceDescription}
                      required={currentDraftStatus === 'implemented'}
                      rows={4}
                      disabled={savingEntryId === countermeasure.requirementId}
                      onChange={event => {
                        setDraftEvidenceDescription(event.target.value);
                        setFormError(null);
                      }}
                    />
                  </label>
                )}
              </div>
            )}
            {currentDraftStatus === 'not_applicable' && (
              <label className="application-compliance-field application-compliance-justification-field">
                <span>Justification</span>
                <textarea
                  value={draftNaJustification}
                  required
                  rows={4}
                  disabled={savingEntryId === countermeasure.requirementId}
                  onChange={event => {
                    setDraftNaJustification(event.target.value);
                    setFormError(null);
                  }}
                />
              </label>
            )}

            <button
              type="submit"
              className="action-button"
              disabled={savingEntryId === countermeasure.requirementId}
            >
              {submitButtonLabel}
            </button>
            {(formError || rowError) && (
              <p className="application-compliance-form-error">
                {formError || rowError}
              </p>
            )}
          </form>
        )}
        <dl className="evaluation-fields application-compliance-audit-fields">
          <div className="evaluation-field">
            <dt>Last updated by:</dt>
            <dd>{formatAuditActor(countermeasure.updatedBy)}</dd>
          </div>
          <div className="evaluation-field">
            <dt>Last updated at:</dt>
            <dd>{formatAuditTimestamp(countermeasure.updatedAt)}</dd>
          </div>
        </dl>
        {renderHistory(countermeasure)}
        {/* State 2: Exception case exists - show conversation panel */}
        {countermeasure.exceptionCase && renderExceptionCase(countermeasure)}
      </div>
    );
  };

  const renderThreatGroup = (sectionKey: string, group: ThreatGroup) => {
    const groupRef = `${sectionKey}:application-compliance-threat:${group.usecase.ref}:${group.threat.ref}`;
    const isOpen = isRequirementFilterActive || expandedRefs.has(groupRef);
    return (
      <div key={groupRef} className="application-compliance-group">
        <button
          type="button"
          className="application-compliance-group-toggle"
          aria-expanded={isOpen}
          onClick={() => toggleExpanded(groupRef)}
        >
          <span>{group.threat.name}</span>
          {renderProgress(
            group.allCountermeasures ?? group.countermeasures,
            true,
          )}
        </button>
        {isOpen && (
          <div className="application-compliance-group-body">
            {group.threat.text && <p>{group.threat.text}</p>}
            <div className="application-compliance-requirements">
              {group.countermeasures.map(renderCountermeasureRow)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderApplicableContent = (section: ComplianceSection) => {
    const sectionKey = section.key;
    const countermeasures = section.countermeasures;
    const countermeasureByRef = new Map(
      countermeasures.map(item => [item.ref, item]),
    );
    const usecases = Array.from(
      new Map(section.usecases.map(usecase => [usecase.ref, usecase])).values(),
    );
    const groupKeyByRef = new Map<string, string>();
    const groupsByKey = new Map<string, ThreatGroup>();

    usecases.forEach(usecase => {
      usecase.threats.forEach(threat => {
        const key = `${usecase.ref}:${threat.ref}`;
        groupsByKey.set(key, { usecase, threat, countermeasures: [] });
      });
    });

    countermeasures.forEach(countermeasure => {
      if (countermeasure.usecaseRef && countermeasure.threatRef) {
        const explicitKey = `${countermeasure.usecaseRef}:${countermeasure.threatRef}`;
        if (groupsByKey.has(explicitKey)) {
          groupKeyByRef.set(countermeasure.ref, explicitKey);
        }
      }
    });

    usecases.forEach(usecase => {
      usecase.threats.forEach(threat => {
        const key = `${usecase.ref}:${threat.ref}`;
        threat.countermeasureRefs.forEach(reference => {
          if (
            countermeasureByRef.has(reference.ref) &&
            !groupKeyByRef.has(reference.ref)
          ) {
            groupKeyByRef.set(reference.ref, key);
          }
        });
      });
    });

    groupKeyByRef.forEach((groupKey, ref) => {
      const countermeasure = countermeasureByRef.get(ref);
      const group = groupsByKey.get(groupKey);
      if (countermeasure && group) {
        group.countermeasures.push(countermeasure);
      }
    });

    const renderedUsecases = usecases
      .map(usecase => {
        const groups = usecase.threats
          .map(threat => groupsByKey.get(`${usecase.ref}:${threat.ref}`))
          .map(group => {
            if (!group || group.countermeasures.length === 0) return null;
            const matchingCountermeasures =
              group.countermeasures.filter(matchesRequirement);
            if (matchingCountermeasures.length === 0) return null;
            return isRequirementFilterActive
              ? {
                  ...group,
                  countermeasures: matchingCountermeasures,
                  allCountermeasures: group.countermeasures,
                }
              : group;
          })
          .filter((group): group is ThreatGroup => Boolean(group));
        if (groups.length === 0) return null;
        const usecaseToggleRef = `${sectionKey}:${usecase.ref}`;
        const isOpen =
          isRequirementFilterActive || expandedRefs.has(usecaseToggleRef);
        const usecaseCountermeasures = groups.reduce<
          ApplicationCountermeasure[]
        >(
          (items, group) =>
            items.concat(group.allCountermeasures ?? group.countermeasures),
          [],
        );
        return (
          <div
            key={usecaseToggleRef}
            className="application-compliance-usecase"
          >
            <button
              type="button"
              className="application-compliance-usecase-toggle"
              aria-expanded={isOpen}
              onClick={() => toggleExpanded(usecaseToggleRef)}
            >
              <span>{usecase.name}</span>
              {renderProgress(usecaseCountermeasures, true)}
            </button>
            {isOpen && (
              <div className="application-compliance-usecase-body">
                {usecase.text && <p>{usecase.text}</p>}
                {groups.map(group => renderThreatGroup(sectionKey, group))}
              </div>
            )}
          </div>
        );
      })
      .filter(Boolean);

    const assignedRefs = new Set(groupKeyByRef.keys());
    const allUnassigned = countermeasures.filter(
      item => !assignedRefs.has(item.ref),
    );
    const unassigned = allUnassigned.filter(matchesRequirement);
    if (unassigned.length > 0) {
      renderedUsecases.push(
        <div
          key={`${sectionKey}:application-compliance-unassigned`}
          className="application-compliance-usecase"
        >
          <div className="application-compliance-usecase-toggle application-compliance-static-heading">
            <span>Unassigned</span>
            {renderProgress(allUnassigned, true)}
          </div>
          <div className="application-compliance-usecase-body">
            <div className="application-compliance-requirements">
              {unassigned.map(renderCountermeasureRow)}
            </div>
          </div>
        </div>,
      );
    }

    if (renderedUsecases.length === 0) {
      return (
        <p className="compliance-empty-state">
          {isRequirementFilterActive
            ? 'No requirements match your search.'
            : 'No active requirements apply to this application.'}
        </p>
      );
    }
    return (
      <div className="application-compliance-usecases">{renderedUsecases}</div>
    );
  };

  const currentCountermeasure = currentRequirementId
    ? findCountermeasure(response, currentRequirementId)
    : null;
  const canRequestException = Boolean(
    canEdit &&
      currentCountermeasure?.automation?.managed &&
      !currentCountermeasure?.exceptionCase,
  );
  const sections = sectionsFromResponse(response);
  const hasAnySection = sections.length > 0;

  return (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button
          ref={detailBackButtonRef}
          type="button"
          className="back-button"
          onClick={
            isRequirementDetail
              ? () => {
                  setHistoryExpanded(false);
                  navigate(
                    `/business-applications/${encodeURIComponent(
                      ban,
                    )}/compliance`,
                  );
                }
              : onBackToDashboard
          }
        >
          <span aria-hidden="true">&#8592;</span>
          {isRequirementDetail
            ? ' Back to Requirements'
            : ' Back to Application Modules'}
        </button>
        <div className="application-compliance-title-row">
          <h4 className="detail-view-title">Compliance</h4>
          <div className="application-compliance-header-buttons">
            {canRequestException && (
              <button
                type="button"
                className="action-button application-compliance-request-exception-button"
                onClick={() => setShowExceptionForm(true)}
              >
                Request an exception
              </button>
            )}
            {!canEdit &&
              linkResponse &&
              !loadingLinks &&
              !isRequirementDetail &&
              !linkError && (
                <span className="application-compliance-link-readonly">
                  Linked to:{' '}
                  {linkResponse.members
                    .filter(m => m.ban !== ban)
                    .map(m => m.name)
                    .join(', ')}
                </span>
              )}
            {canEdit && !loadingLinks && !isRequirementDetail && !linkError && (
              <button
                type="button"
                className="action-button application-compliance-link-button"
                disabled={savingLink}
                onClick={
                  linkResponse
                    ? () => void handleDeleteApplicationLink()
                    : () => setShowLinkPicker(true)
                }
              >
                {linkResponse
                  ? `Linked to: ${linkResponse.members
                      .filter(m => m.ban !== ban)
                      .map(m => m.name)
                      .join(', ')} · Unlink`
                  : 'Connect the compliance status of this application with other applications'}
              </button>
            )}
            {!loadingLinks && linkError && (
              <span
                className="application-compliance-link-error-notice"
                title={linkError}
              >
                Link status unavailable
              </span>
            )}
          </div>
        </div>
      </div>

      {loading && <p className="loading-text">Loading...</p>}
      {!loading && error && <p className="monitoring-error-notice">{error}</p>}
      {!loading && !error && response && (
        <>
          {showLinkPicker && (
            <div className="application-compliance-link-picker">
              <button
                type="button"
                className="application-compliance-link-picker-backdrop"
                aria-label="Close dialog"
                onClick={resetLinkPickerState}
              />
              <div
                className="application-compliance-link-picker-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Link Applications"
              >
                <div className="application-compliance-link-picker-header">
                  <h3>Link Applications</h3>
                  <button
                    type="button"
                    className="application-compliance-link-picker-close"
                    onClick={resetLinkPickerState}
                  >
                    ×
                  </button>
                </div>

                <div className="application-compliance-link-picker-body">
                  {linkStep === 'search' && (
                    <div className="application-compliance-link-picker-step">
                      <div className="application-compliance-field">
                        <label htmlFor="application-link-ban-search">
                          Search for an application by BAN:
                        </label>
                        <input
                          id="application-link-ban-search"
                          type="text"
                          value={banSearchTerm}
                          placeholder="e.g., APM0001234"
                          disabled={savingLink}
                          onChange={event => {
                            setBanSearchTerm(event.target.value);
                            setLinkFormError(null);
                          }}
                        />
                      </div>
                      <div className="application-link-target-list">
                        {loadingBanSearch && (
                          <p className="application-link-target-list-notice">
                            Searching...
                          </p>
                        )}
                        {!loadingBanSearch &&
                          banSearchTerm.trim() &&
                          banSearchResults.length === 0 && (
                            <p className="application-link-target-list-notice">
                              No applications found for that BAN.
                            </p>
                          )}
                        {!loadingBanSearch &&
                          banSearchResults.map(result => (
                            <div
                              key={result.ban}
                              className={`application-link-target-option ${
                                targetBan === result.ban ? 'selected' : ''
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleSelectTargetBan(result)}
                              onKeyDown={event => {
                                if (
                                  event.key === 'Enter' ||
                                  event.key === ' '
                                ) {
                                  event.preventDefault();
                                  handleSelectTargetBan(result);
                                }
                              }}
                            >
                              <strong>{result.ban}</strong>
                              {result.name && <span> — {result.name}</span>}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {linkStep === 'import' && (
                    <div className="application-compliance-link-picker-step">
                      <p>Import compliance status from which application?</p>
                      <div className="application-compliance-import-options">
                        <label className="application-compliance-radio-option">
                          <input
                            type="radio"
                            name="importFrom"
                            value={ban}
                            checked={importFrom === ban}
                            disabled={savingLink}
                            onChange={event => {
                              setImportFrom(event.target.value);
                              setLinkFormError(null);
                            }}
                          />
                          <span>Current application ({ban})</span>
                        </label>
                        <label className="application-compliance-radio-option">
                          <input
                            type="radio"
                            name="importFrom"
                            value={targetBan}
                            checked={importFrom === targetBan}
                            disabled={savingLink}
                            onChange={event => {
                              setImportFrom(event.target.value);
                              setLinkFormError(null);
                            }}
                          />
                          <span>
                            Target application ({targetBan}
                            {targetBanName ? ` — ${targetBanName}` : ''})
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="application-compliance-link-picker-footer">
                  {linkFormError && (
                    <p className="application-compliance-form-error">
                      {linkFormError}
                    </p>
                  )}
                  {linkStep === 'search' && (
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={savingLink}
                      onClick={resetLinkPickerState}
                    >
                      Cancel
                    </button>
                  )}
                  {linkStep === 'import' && (
                    <>
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={savingLink}
                        onClick={() => setLinkStep('search')}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="action-button"
                        disabled={savingLink || !importFrom.trim()}
                        onClick={() => void handleCreateApplicationLink()}
                      >
                        {savingLink ? 'Linking...' : 'Confirm Link'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <Routes>
            <Route
              index
              element={
                <>
                  {!response.baseline.applicable && (
                    <div className="not-applicable-card">
                      <span className="not-applicable-icon" aria-hidden="true">
                        &#8416;
                      </span>
                      <p className="not-applicable-text">
                        Baseline Requirements are not applicable to this
                        application.{' '}
                        {response.baseline.reason === 'unmapped_app_type'
                          ? 'Its application type could not be mapped to the compliance library.'
                          : 'Its security impact is unassigned or does not map to a Baseline level.'}
                        {response.assignedStandards.length > 0
                          ? ' The Standards assigned to it are shown below.'
                          : ''}
                      </p>
                    </div>
                  )}
                  {hasAnySection && (
                    <div
                      className="application-compliance-search"
                      role="search"
                    >
                      <label
                        className="application-compliance-search-label"
                        htmlFor="application-compliance-requirement-search"
                      >
                        Find a requirement
                      </label>
                      <div className="application-compliance-search-control">
                        <input
                          id="application-compliance-requirement-search"
                          className="application-compliance-search-input"
                          type="search"
                          value={requirementSearch}
                          placeholder="Search by name, ID, or description"
                          onChange={event =>
                            setRequirementSearch(event.target.value)
                          }
                        />
                        <FormControl
                          variant="outlined"
                          size="small"
                          className="application-compliance-label-filter"
                        >
                          <InputLabel id="application-compliance-label-filter-label">
                            Label
                          </InputLabel>
                          <Select
                            labelId="application-compliance-label-filter-label"
                            id="application-compliance-label-filter"
                            label="Label"
                            value={requirementLabelFilter}
                            onChange={event =>
                              setRequirementLabelFilter(
                                event.target.value as RequirementLabelFilter,
                              )
                            }
                          >
                            {LABEL_FILTER_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {isRequirementFilterActive && (
                          <button
                            type="button"
                            className="application-compliance-search-clear"
                            onClick={() => {
                              setRequirementSearch('');
                              setRequirementLabelFilter('');
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {sections.map(section => (
                    <section
                      key={section.key}
                      className="application-compliance-standard-section"
                      aria-labelledby={`${section.key}-heading`}
                    >
                      <div className="application-compliance-standard-section-header">
                        <h5
                          id={`${section.key}-heading`}
                          className="application-compliance-standard-section-title"
                        >
                          {section.title}
                        </h5>
                        {section.key === 'baseline' &&
                          response.baseline.appType &&
                          response.baseline.impactLevelId && (
                            <p className="application-compliance-summary">
                              Application type{' '}
                              <strong>{response.baseline.appType}</strong> and
                              security impact{' '}
                              <strong>{response.baseline.impactLevelId}</strong>
                            </p>
                          )}
                        <div className="application-compliance-overall-progress">
                          {renderProgress(section.countermeasures)}
                        </div>
                      </div>
                      {renderApplicableContent(section)}
                    </section>
                  ))}
                  {!hasAnySection && (
                    <p className="compliance-empty-state">
                      No Baseline Requirements or assigned Standards apply to
                      this application.
                    </p>
                  )}
                </>
              }
            />
            <Route
              path=":requirementId"
              element={
                <RequirementDetailRoute
                  response={response}
                  detailBackButtonRef={detailBackButtonRef}
                  renderRequirementDetail={renderRequirementDetail}
                  setDraftStatus={setDraftStatus}
                  setDraftSupportingLinks={setDraftSupportingLinks}
                  setDraftEvidenceDescription={setDraftEvidenceDescription}
                  setDraftNaJustification={setDraftNaJustification}
                  setFormError={setFormError}
                />
              }
            />
          </Routes>
        </>
      )}
    </div>
  );
}
